import { useCallback, useEffect, useState } from "react";
import { ErrorBanner, Skeleton, useConfirm } from "./ui-kit";
import { formatServerDateTime } from "./server-time";

type Connection = { cloudId: string; id: string; siteName: string; siteUrl: string; updatedAt: string };
type ImportJob = {
  boardId: number; completedAt: string | null; connectionId: string; createdAt: string; errorMessage: string | null;
  id: string; importedIssueCount: number; importedSprintCount: number; skippedIssueCount: number;
  importTarget: "backlog" | "sprint"; jiraSprintId: number | null;
  status: "pending" | "running" | "completed" | "failed"; syncedStatusCount: number;
  warningMessage: string | null;
};
type JiraBoard = { id: number; name: string; type: string };
type JiraSprint = { completeDate?: string; id: number; name: string; state: "active" | "closed" };
type JobIssue = {
  jiraAssigneeEmail: string | null;
  jiraIssueKey: string;
  resolution: "matched" | "fallback_hidden_email" | "fallback_unmatched_email" | "fallback_unassigned";
  resolvedUserName: string | null;
  taskKey: string | null;
  title: string | null;
};

const RESOLUTION_LABEL: Record<JobIssue["resolution"], string> = {
  matched: "Matched by email",
  fallback_hidden_email: "Assignee's email is hidden",
  fallback_unassigned: "Issue has no Jira assignee",
  fallback_unmatched_email: "Assignee's email has no Taskei account",
};

const actionableImportError = (message: string | null) =>
  message && /(scope does not match|read:jql:jira scope)/i.test(message)
    ? "The original Jira sprint endpoint rejected this token. Taskei can now retry through Jira issue search; reconnect only if the retry is also refused."
    : message;

const request = async <T,>(path: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(path, {
    ...init,
    headers: init?.body ? { "content-type": "application/json", ...init.headers } : init?.headers,
  });
  const payload = (await response.json()) as T & { code?: string; message?: string };
  if (!response.ok) {
    const problem = new Error(payload.message || "Taskei could not complete that request.") as Error & { code?: string };
    problem.code = payload.code;
    throw problem;
  }
  return payload;
};

function JiraImportJobIssues({ jobId }: { jobId: string }) {
  const [issues, setIssues] = useState<JobIssue[] | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    request<{ issues: JobIssue[] }>(`/api/integrations/jira/imports/${encodeURIComponent(jobId)}/issues`)
      .then((result) => { if (!cancelled) setIssues(result.issues); })
      .catch((reason) => { if (!cancelled) setError(reason instanceof Error ? reason.message : "Could not load import details."); });
    return () => { cancelled = true; };
  }, [jobId]);
  if (error) return <ErrorBanner>{error}</ErrorBanner>;
  if (!issues) return <Skeleton label="Loading import details…" />;
  if (!issues.length) return <p className="admin-help">No newly-imported issues were recorded for this job.</p>;
  const hiddenEmailCount = issues.filter((issue) => issue.resolution === "fallback_hidden_email").length;
  return (
    <div className="jira-job-issues">
      {hiddenEmailCount > 0 && (
        <p className="admin-help">
          Email visibility is controlled by each assignee's personal Atlassian privacy setting (and/or your Jira
          instance's "User email visibility" setting) — not by Taskei's OAuth permissions. Ask the affected
          person, or your Jira site admin, to set profile visibility to "Organisation" or "Anyone."
        </p>
      )}
      <div className="simple-list">
        {issues.map((issue) => (
          <article key={issue.jiraIssueKey}>
            <div><strong>{issue.taskKey ?? issue.jiraIssueKey}</strong> {issue.title ?? issue.jiraIssueKey}</div>
            <small>{issue.jiraAssigneeEmail ?? "No Jira email"} → {issue.resolvedUserName ?? "Unresolved"} · {RESOLUTION_LABEL[issue.resolution]}</small>
          </article>
        ))}
      </div>
    </div>
  );
}

export function JiraImportPanel({ teamId }: { teamId: string }) {
  const confirm = useConfirm();
  const [boards, setBoards] = useState<JiraBoard[]>([]);
  const [busy, setBusy] = useState("");
  const [connections, setConnections] = useState<Connection[]>([]);
  const [error, setError] = useState("");
  const [expandedJobId, setExpandedJobId] = useState("");
  const [jobs, setJobs] = useState<ImportJob[]>([]);
  const [notConfigured, setNotConfigured] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState("");
  const [selectedConnection, setSelectedConnection] = useState("");
  const [selectedTarget, setSelectedTarget] = useState("backlog");
  const [sprints, setSprints] = useState<JiraSprint[]>([]);

  const load = useCallback(async () => {
    try {
      const result = await request<{ connections: Connection[]; jobs: ImportJob[] }>(
        `/api/integrations/jira?teamId=${encodeURIComponent(teamId)}`,
      );
      setConnections(result.connections); setJobs(result.jobs); setNotConfigured(false);
      setError("");
      setSelectedConnection((current) => current || result.connections[0]?.id || "");
    } catch (reason) {
      const problem = reason as Error & { code?: string };
      if (problem.code === "JIRA_NOT_CONFIGURED") setNotConfigured(true);
      else setError(problem.message);
    }
  }, [teamId]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!jobs.some(({ status }) => status === "pending" || status === "running")) return;
    const interval = window.setInterval(() => {
      // A large import can outgrow a single Worker invocation's subrequest
      // budget, so the server processes it in bounded chunks and leaves the
      // job "running" between them. Nudge every still-running job forward
      // here so the import keeps progressing while this panel is open.
      const running = jobs.filter(({ status }) => status === "running");
      void Promise.all(
        running.map(({ id }) =>
          request(`/api/integrations/jira/imports/${encodeURIComponent(id)}/continue`, { method: "POST" }).catch(() => null)),
      ).then(() => load());
    }, 3000);
    return () => window.clearInterval(interval);
  }, [jobs, load]);
  useEffect(() => {
    setBoards([]); setSelectedBoard("");
    if (!selectedConnection) return;
    request<{ boards: JiraBoard[] }>(`/api/integrations/jira/${encodeURIComponent(selectedConnection)}/boards`)
      .then((result) => { setBoards(result.boards); setSelectedBoard(String(result.boards[0]?.id ?? "")); })
      .catch((reason) => setError(reason instanceof Error ? reason.message : "Could not load Jira boards."));
  }, [selectedConnection]);
  useEffect(() => {
    setSprints([]); setSelectedTarget("backlog");
    if (!selectedConnection || !selectedBoard) return;
    request<{ sprints: JiraSprint[] }>(`/api/integrations/jira/${encodeURIComponent(selectedConnection)}/boards/${encodeURIComponent(selectedBoard)}/sprints`)
      .then((result) => setSprints(result.sprints))
      .catch((reason) => setError(reason instanceof Error ? reason.message : "Could not load Jira sprints."));
  }, [selectedBoard, selectedConnection]);

  const connect = async () => {
    setBusy("connect"); setError("");
    try {
      const result = await request<{ authorizeUrl: string }>("/api/integrations/jira/connect", {
        body: JSON.stringify({ teamId }), method: "POST",
      });
      window.location.assign(result.authorizeUrl);
    } catch (reason) {
      setBusy(""); setError(reason instanceof Error ? reason.message : "Could not connect Jira.");
    }
  };
  const startImport = async (retry?: ImportJob) => {
    const connectionId = retry?.connectionId ?? selectedConnection;
    const boardId = retry?.boardId ?? Number(selectedBoard);
    const target = retry?.importTarget ?? (selectedTarget === "backlog" ? "backlog" : "sprint");
    const sprintId = retry?.jiraSprintId ?? (selectedTarget.startsWith("sprint:") ? Number(selectedTarget.slice(7)) : null);
    if (!connectionId || !boardId) return;
    setBusy("import"); setError("");
    try {
      await request("/api/integrations/jira/import", {
        body: JSON.stringify({
          boardId,
          connectionId,
          importTarget: target,
          sprintId,
          teamId,
        }), method: "POST",
      });
      await load();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Could not start Jira import."); }
    finally { setBusy(""); }
  };
  const deleteHistory = async (job: ImportJob) => {
    const source = job.importTarget === "sprint" ? `Sprint ${job.jiraSprintId}` : "Backlog";
    const completed = job.completedAt ? formatServerDateTime(job.completedAt) : "unknown time";
    const ok = await confirm({
      confirmLabel: "Delete history",
      description: "Imported work stays in Taskei. This cannot be undone.",
      title: `Delete Jira import history for ${source}, completed ${completed}?`,
      tone: "danger",
    });
    if (!ok) return;
    setBusy(`delete-${job.id}`); setError("");
    try {
      await request(`/api/integrations/jira/import-jobs/${encodeURIComponent(job.id)}`, { method: "DELETE" });
      await load();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Could not delete the import history."); }
    finally { setBusy(""); }
  };
  const cancelImport = async (job: ImportJob) => {
    const source = job.importTarget === "sprint" ? `Sprint ${job.jiraSprintId}` : "Backlog";
    const ok = await confirm({
      confirmLabel: "Cancel import",
      description: "Issues already imported stay in Taskei. Retry the import afterward to pick up where this left off.",
      title: `Cancel the running import for ${source} · Board ${job.boardId}?`,
      tone: "danger",
    });
    if (!ok) return;
    setBusy(`cancel-${job.id}`); setError("");
    try {
      await request(`/api/integrations/jira/import-jobs/${encodeURIComponent(job.id)}/cancel`, { method: "POST" });
      await load();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Could not cancel the Jira import."); }
    finally { setBusy(""); }
  };

  return (
    <section className="admin-panel wide jira-import-panel">
      <div className="section-heading">
        <div><p className="eyebrow">Migration</p><h2>Import from Jira Cloud</h2><p className="section-description">Connect a Jira site, choose a board, and import its backlog, sprints, issues, estimates, labels, and assignees.</p></div>
        {!notConfigured && <button className="button secondary" disabled={busy === "connect"} onClick={() => void connect()} type="button">{connections.length ? "Reconnect Jira" : "Connect Jira"}</button>}
      </div>
      {notConfigured ? (
        <div className="integration-status integration-status-failed" role="status"><span className="integration-status-dot" /><div><strong>Jira OAuth is not configured</strong><p>Add the Jira client ID, client secret, and callback URI to the production Worker before connecting a site.</p></div></div>
      ) : <>
        {error && <div className="banner-error" role="alert"><span>{error}</span><button className="text-button" onClick={() => void load()} type="button">Retry status</button></div>}
        {connections.length ? <div className="jira-import-controls">
          <label>Jira site<select aria-label="Jira site" onChange={(event) => setSelectedConnection(event.target.value)} value={selectedConnection}>{connections.map((connection) => <option key={connection.id} value={connection.id}>{connection.siteName}</option>)}</select></label>
          <label>Board<select aria-label="Jira board" disabled={!boards.length} onChange={(event) => setSelectedBoard(event.target.value)} value={selectedBoard}><option value="">Choose board</option>{boards.map((board) => <option key={board.id} value={board.id}>{board.name} · {board.type}</option>)}</select></label>
          <label>Import<select aria-label="Jira import target" disabled={!selectedBoard} onChange={(event) => setSelectedTarget(event.target.value)} value={selectedTarget}>
            <option value="backlog">Backlog</option>
            {sprints.map((sprint) => <option key={sprint.id} value={`sprint:${sprint.id}`}>{sprint.name} · {sprint.state === "active" ? "Open" : "Closed"}</option>)}
          </select></label>
          <button className="button primary" disabled={!selectedBoard || busy === "import" || jobs.some(({ status }) => ["pending", "running"].includes(status))} onClick={() => void startImport()} type="button">{busy === "import" ? "Starting…" : selectedTarget === "backlog" ? "Import backlog" : "Import sprint"}</button>
        </div> : <p className="admin-help">Connect Jira to choose a site and board.</p>}
        {jobs.length > 0 && <div className="jira-import-jobs"><h3>Recent imports</h3>{jobs.map((job) => <article className={`jira-job jira-job-${job.status}`} key={job.id}><div><strong>{job.importTarget === "sprint" ? `Sprint ${job.jiraSprintId}` : "Backlog"} · Board {job.boardId}</strong><span>{job.status}</span></div><p>{job.importedIssueCount} imported · {job.skippedIssueCount} already present{job.syncedStatusCount > 0 ? ` (${job.syncedStatusCount} status updated from Jira)` : ""} · {job.importedSprintCount} sprints</p>{job.warningMessage && <small className="jira-job-warning">{job.warningMessage}</small>}{job.errorMessage && <small>{actionableImportError(job.errorMessage)}</small>}<div className="jira-job-actions">{job.status === "completed" && job.importedIssueCount > 0 && <button aria-label={`${expandedJobId === job.id ? "Hide" : "View"} details for import ${job.id}`} className="button secondary" onClick={() => setExpandedJobId((current) => current === job.id ? "" : job.id)} type="button">{expandedJobId === job.id ? "Hide details" : "View details"}</button>}{job.status === "failed" && <button className="button secondary jira-retry" disabled={Boolean(busy)} onClick={() => void startImport(job)} type="button">Retry import</button>}{["pending", "running"].includes(job.status) && <button aria-label={`Cancel import ${job.id}`} className="button danger jira-cancel" disabled={Boolean(busy)} onClick={() => void cancelImport(job)} type="button">{busy === `cancel-${job.id}` ? "Cancelling…" : "Cancel import"}</button>}{["completed", "failed"].includes(job.status) && <button aria-label={`Delete import history ${job.id}`} className="button danger" disabled={Boolean(busy)} onClick={() => void deleteHistory(job)} type="button">Delete history</button>}</div>{expandedJobId === job.id && <JiraImportJobIssues jobId={job.id} />}</article>)}</div>}
      </>}
    </section>
  );
}
